// src/components/shared/ErrorBoundary.tsx

'use client'

import { Component, type ReactNode } from 'react'

import { handleClientError } from '~/lib/client-error-handling'

/**
 * Props for the ErrorBoundary component.
 * 
 * @interface Props
 * @property {ReactNode} children - The components to be rendered and monitored for errors.
 * @property {ReactNode | ((error: Error) => ReactNode)} [fallback] - Component or function to render when an error occurs.
 *   If a function is provided, it will receive the error as an argument.
 */
interface Props {
  children: ReactNode
  fallback?: ReactNode | ((error: Error) => ReactNode)
  /**
   * Optional label for the drain's `component` field so a caught fault is
   * attributable to a specific boundary (e.g. `'fake-chat-preview'`) rather than
   * the generic `'ErrorBoundary'`. Defaults to `'ErrorBoundary'` so existing
   * callers are unaffected.
   */
  name?: string
}

/**
 * State for the ErrorBoundary component.
 * 
 * @interface State
 * @property {boolean} hasError - Indicates whether an error has been caught.
 * @property {Error} [error] - The error that was caught, if any.
 */
interface State {
  hasError: boolean
  error?: Error
}

/**
 * Default fallback UI to display when an error occurs and no custom fallback is provided.
 * 
 * @param {Error} [error] - The error that was caught.
 * @returns {ReactNode} A simple error message UI.
 */
function DefaultErrorFallback({ error }: { error?: Error }): ReactNode {
  return (
    <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/10">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
            Something went wrong
          </h3>
          {error && (
            <div className="mt-2 text-sm text-red-700 dark:text-red-300">
              {error.message}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Renders the appropriate fallback UI based on the provided fallback prop.
 * 
 * @param {Object} props - Component props.
 *   @param {Error} [props.error] - The error that was caught.
 *   @param {ReactNode | ((error: Error) => ReactNode)} [props.fallback] - Custom fallback UI or function.
 * @returns {ReactNode} The fallback UI to render.
 */
function ErrorFallback({ 
  error, 
  fallback 
}: { 
  error?: Error 
  fallback?: ReactNode | ((error: Error) => ReactNode) 
}): ReactNode {
  // If fallback is a function and we have an error, call it with the error
  if (typeof fallback === 'function' && error) {
    return fallback(error)
  }
  
  // If fallback is provided as a ReactNode, return it
  if (fallback) {
    return fallback as ReactNode
  }
  
  // Otherwise return the default fallback
  return <DefaultErrorFallback error={error} />
}

/**
 * A component that catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 * 
 * This component follows the Error Boundary pattern introduced in React 16.
 * 
 * @example
 * // Basic usage with default fallback UI
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * 
 * @example
 * // With custom fallback component
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <MyComponent />
 * </ErrorBoundary>
 * 
 * @example
 * // With function that receives the error
 * <ErrorBoundary fallback={(error) => <CustomErrorUI message={error.message} />}>
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  /**
   * Initialize the component with default state.
   * 
   * @param {Props} props - Component props.
   */
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  /**
   * Update state when an error occurs during rendering.
   * This is called during the "render" phase, so side-effects are not permitted.
   * 
   * @param {Error} error - The error that was thrown.
   * @returns {State} New state to be applied.
   */
  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    }
  }

  /**
   * Called after an error has been thrown by a descendant component.
   * Use this method for logging errors or sending them to an error reporting service.
   * 
   * @param {Error} error - The error that was thrown.
   * @param {React.ErrorInfo} errorInfo - Information about the component stack.
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Classify, log to the drain (structured `client_error` with stack +
    // component stack), and attempt one-shot boundary recovery for transient
    // chunk/DOM faults. Mirrors ResourceErrorBoundary.
    handleClientError(
      error,
      {
        type: 'error_boundary',
        component: this.props.name ?? 'ErrorBoundary',
        componentStack: errorInfo.componentStack,
      },
      // Log-only: this PR migrates logging without changing recovery UX. The
      // pre-existing behavior was console + inline fallback (no auto-reload), so
      // `recover: false` preserves it. Auto-recovery can be opted in later.
      { recover: false },
    )
  }

  /**
   * Render either the children or the fallback UI based on whether an error occurred.
   * 
   * @returns {ReactNode} The rendered output.
   */
  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} fallback={this.props.fallback} />
    }

    return this.props.children
  }
}